import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg } from "@/lib/api";
import { fmt } from "@/lib/status";

const LABEL = { patients: "Patients", packets: "Packets", templates: "Forms", appointments: "Visits", files: "Files", audit: "Audit events", signing_sessions: "Signing links", consents: "Consents" };

function Verdict({ ok, children, testid }) {
  const I = ok ? ShieldCheck : ShieldAlert;
  return <span className={`inline-flex items-center gap-1 font-semibold ${ok ? "text-emerald-700" : "text-red-700"}`} data-testid={testid}><I className="w-4 h-4" />{children}</span>;
}

export default function IsolationCheck() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/enterprise/isolation-check").then((x) => setR(x.data?.id ? x.data : null)).catch(() => {}); }, []);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/enterprise/isolation-check"); setR(data); toast[data.result === "pass" ? "success" : "error"](data.result === "pass" ? "Every organization is isolated" : "Problems found - see below"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="isolation-check">
      <div className="bg-white border rounded-xl p-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[260px]"><p className="text-sm font-semibold">Organization isolation check</p>
          <p className="text-xs text-slate-500">Read-only scan: every organization's storage holds only its own records, and no packet, visit, consent or signing link points to another organization. Nothing is changed.</p></div>
        <Button className="btn-brand" disabled={busy} onClick={run} data-testid="isolation-run-btn">{busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Checking...</> : "Run check now"}</Button>
      </div>
      {!r && <p className="text-sm text-slate-500" data-testid="isolation-never-run">No check has been run yet.</p>}
      {r && <>
        <p className="text-sm" data-testid="isolation-summary"><Verdict ok={r.result === "pass"} testid="isolation-result">{r.result === "pass" ? "Pass" : "Fail"}</Verdict>
          <span className="text-slate-500 ml-2">Last run {fmt(r.at)} by {r.by_name}</span></p>
        {r.shared_problems?.length > 0 && <ul className="text-sm text-red-700 list-disc pl-5">{r.shared_problems.map((p) => <li key={p}>{p}</li>)}</ul>}
        <div className="overflow-x-auto"><Table className="bg-white border rounded-xl"><TableHeader><TableRow><TableHead>Organization</TableHead><TableHead>Result</TableHead><TableHead>Records checked</TableHead><TableHead>Problems</TableHead></TableRow></TableHeader>
          <TableBody>{r.organizations.map((o) => (
            <TableRow key={o.tenant_id} data-testid={`isolation-row-${o.tenant_id}`}>
              <TableCell><b className="text-sm">{o.organization}</b><div className="text-[11px] text-slate-500 font-mono">{o.storage}</div></TableCell>
              <TableCell><Verdict ok={o.result === "pass"} testid={`isolation-org-result-${o.tenant_id}`}>{o.result === "pass" ? "Pass" : "Fail"}</Verdict></TableCell>
              <TableCell className="text-xs">{Object.entries(LABEL).map(([k, l]) => `${l} ${o.records[k] ?? 0}`).join(" - ")}</TableCell>
              <TableCell className="text-xs">{o.problems.length ? <ul className="text-red-700 list-disc pl-4">{o.problems.map((p) => <li key={p}>{p}</li>)}</ul> : <span className="text-slate-500">None</span>}
                {o.notes?.length > 0 && <ul className="text-amber-700 list-disc pl-4 mt-1" data-testid={`isolation-notes-${o.tenant_id}`}>{o.notes.map((p) => <li key={p}>{p}</li>)}</ul>}</TableCell>
            </TableRow>))}</TableBody></Table></div>
      </>}
    </div>
  );
}
